// directive-inbox.ts
//
// Control commands (approve/pause/resume/answer/redirect/revert/abort) write
// ONLY here — atomic, one file per directive under `<control_root>/inbox/` —
// NEVER touch mission.json directly (spec §3.3/§7.3, test 26). Dedupe by
// directive/request id (test 28). When the supervisor isn't running, returns
// a `queued` receipt (test 27) — checked via readMachineLock READ-ONLY
// (acquiring would contend with a real running supervisor).

import { createHash } from "node:crypto";
import { join } from "node:path";
import { ensureDir, pathExists, readJson } from "../filesystem-helpers.js";
import { writeFileDurable } from "./durable-file-writer.js";
import { readMachineLock } from "./mission-lock.js";
import { realLivenessProbe } from "./process-liveness-probe.js";
import { type Directive, DirectiveSchema } from "./schemas/directive-schema.js";

export interface DirectiveReceipt {
  directive_id: string;
  status: "accepted" | "queued" | "duplicate";
  reason?: string;
}

export interface WriteDirectiveInput {
  controlRoot: string;
  machineLockPath: string;
  directive: Omit<Directive, "directive_id" | "received_at" | "schema_version"> & {
    directive_id?: string;
    received_at?: string;
  };
  now?: () => Date;
}

/** Deterministic directive id from mission/kind/actor/payload — dedupe key when caller omits one. */
function deriveDirectiveId(directive: WriteDirectiveInput["directive"]): string {
  const canonical = JSON.stringify({
    mission_id: directive.mission_id,
    kind: directive.kind,
    actor_id: directive.actor_id,
    payload: directive.payload,
    source_post_id: directive.source_post_id,
  });
  return `dir-${createHash("sha256").update(canonical, "utf8").digest("hex").slice(0, 16)}`;
}

function inboxDir(controlRoot: string): string {
  return join(controlRoot, "inbox");
}

/**
 * Security F-01: directive `chat_delegated` ghi đuôi `.delegated` — 3.37 chỉ liệt kê
 * `.json` nên bản hạ cấp BỎ QUA thay vì áp như người. 3.38 đọc cả hai.
 */
export const DELEGATED_DIRECTIVE_SUFFIX = ".delegated";
const INBOX_SUFFIXES = [".json", DELEGATED_DIRECTIVE_SUFFIX] as const;

function directivePaths(controlRoot: string, directiveId: string): [string, string] {
  const at = (suffix: string) => join(inboxDir(controlRoot), `${directiveId}${suffix}`);
  return [at(INBOX_SUFFIXES[0]), at(INBOX_SUFFIXES[1])];
}

/**
 * Write a directive atomically to the inbox. Never mutates mission.json or
 * any projection — this function only knows about `inbox/`.
 */
export async function writeDirective(input: WriteDirectiveInput): Promise<DirectiveReceipt> {
  const now = input.now ?? (() => new Date());
  const directiveId = input.directive.directive_id ?? deriveDirectiveId(input.directive);

  const [jsonPath, delegatedPath] = directivePaths(input.controlRoot, directiveId);
  const path = input.directive.source === "chat_delegated" ? delegatedPath : jsonPath;
  if ((await pathExists(jsonPath)) || (await pathExists(delegatedPath))) {
    return {
      directive_id: directiveId,
      status: "duplicate",
      reason: "request đã được ghi trước đó",
    };
  }

  const full: Directive = DirectiveSchema.parse({
    schema_version: 1,
    directive_id: directiveId,
    mission_id: input.directive.mission_id,
    kind: input.directive.kind,
    source: input.directive.source,
    actor_id: input.directive.actor_id,
    received_at: input.directive.received_at ?? now().toISOString(),
    payload: input.directive.payload,
    source_post_id: input.directive.source_post_id,
  });

  await ensureDir(inboxDir(input.controlRoot));
  await writeFileDurable(path, Buffer.from(`${JSON.stringify(full, null, 2)}\n`, "utf8"));

  const supervisorRunning = await isSupervisorRunning(
    input.machineLockPath,
    input.directive.mission_id,
  );
  return {
    directive_id: directiveId,
    status: supervisorRunning ? "accepted" : "queued",
    reason: supervisorRunning
      ? undefined
      : "Supervisor chưa chạy — directive nằm trong inbox, chạy 'avatar autopilot run' để xử lý.",
  };
}

/**
 * H21 (close-out 2026-09-30): "accepted" means THIS mission's supervisor is
 * alive to read the inbox — not merely that some lock file exists (another
 * mission's, or one left by a supervisor that died).
 */
async function isSupervisorRunning(machineLockPath: string, missionId: string): Promise<boolean> {
  const lock = await readMachineLock(machineLockPath);
  if (lock === undefined || lock.missionId !== missionId) return false;
  return realLivenessProbe.isAlive(lock.pid);
}

/** Nội dung thô của file inbox: `undefined` = không có; JSON hỏng ⇒ `null` (F-02c từ chối). */
export async function readInboxDirectiveRaw(
  controlRoot: string,
  directiveId: string,
): Promise<unknown> {
  for (const path of directivePaths(controlRoot, directiveId)) {
    if (!(await pathExists(path))) continue;
    try {
      return await readJson<unknown>(path);
    } catch {
      return null;
    }
  }
  return undefined;
}

/** F-02(c): chỉ trả directive ĐÚNG schema (nguồn/kind lạ ⇒ `undefined`; drain journal từ chối). */
export async function readDirective(
  controlRoot: string,
  directiveId: string,
): Promise<Directive | undefined> {
  const parsed = DirectiveSchema.safeParse(await readInboxDirectiveRaw(controlRoot, directiveId));
  return parsed.success ? parsed.data : undefined;
}

export async function listInboxDirectiveIds(controlRoot: string): Promise<string[]> {
  const { promises: fs } = await import("node:fs");
  const dir = inboxDir(controlRoot);
  if (!(await pathExists(dir))) return [];
  const ids = new Set<string>();
  for (const entry of await fs.readdir(dir)) {
    const suffix = INBOX_SUFFIXES.find((s) => entry.endsWith(s));
    if (suffix !== undefined) ids.add(entry.slice(0, -suffix.length));
  }
  return [...ids];
}

// P7 (test 25): a redirect/answer/revert directive is applied at a tick
// BOUNDARY only (never mid-attempt — the current worker always drains
// first), by a separate step (directive-redirect-application.ts). This
// marker prevents that step from re-applying the same directive on every
// subsequent tick once it has been handled once — distinct from the
// directive file itself (which stays as the durable record of what was
// asked), and distinct from the redirect-apply operation_id idempotency
// (that guards the ticket-transaction; this guards "don't even try again").
function processedMarkerPath(controlRoot: string, directiveId: string): string {
  return join(inboxDir(controlRoot), `${directiveId}.processed`);
}

export async function isDirectiveProcessed(
  controlRoot: string,
  directiveId: string,
): Promise<boolean> {
  return pathExists(processedMarkerPath(controlRoot, directiveId));
}

export async function markDirectiveProcessed(
  controlRoot: string,
  directiveId: string,
  now: () => Date = () => new Date(),
): Promise<void> {
  await writeFileDurable(
    processedMarkerPath(controlRoot, directiveId),
    Buffer.from(`${now().toISOString()}\n`, "utf8"),
  );
}

// P7 (tests 20-24): Mattermost poll -> command-parser -> writeDirective
// wiring lives in its own module (file-size convention) — re-exported here
// so callers only need one import path for "directive-inbox" concerns.
export { syncMattermostDirectivesIntoInbox } from "./directive-inbox-mattermost-sync.js";
